// src/pages/attendance/hooks/useAttendanceData.js
import { useState, useEffect, useMemo } from "react";
import { CheckCircle, XCircle, AlertCircle, Timer } from "lucide-react";
import { apiFetch } from "../../../context/AuthContext";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const WEEK_DAYS = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];

export const useAttendanceData = ({ currentUser, infinityCode: infinityCodeProp, language, t, colors }) => {
  const [attendanceStats,      setAttendanceStats]      = useState([]);
  const [loadingStats,         setLoadingStats]         = useState(true);
  const [weeklyData,           setWeeklyData]           = useState([]);
  const [monthlyAttendance,    setMonthlyAttendance]    = useState([]);
  const [timeEntries,          setTimeEntries]          = useState([]);
  const [isLoadingTimeEntries, setIsLoadingTimeEntries] = useState(true);
  const [todayAttendanceRecord,setTodayAttendanceRecord]= useState(null);

  // Prefer profile-supplied value; fall back to localStorage only if not provided
  const employeeId = infinityCodeProp || localStorage.getItem("infinityCode");

  // today record
  const fetchTodayAttendanceRecord = async () => {
    try {
      if (!employeeId) return;
      const today = new Date().toISOString().split("T")[0];
      const res = await apiFetch(`${BASE_URL}/attendance/${employeeId}/day?date=${encodeURIComponent(today)}`, { method: "GET" });
      if (!res?.ok) { setTodayAttendanceRecord(null); return; }
      const result = await res.json();
      setTodayAttendanceRecord(result?.success ? result.data || null : null);
    } catch { setTodayAttendanceRecord(null); }
  };

  // recent entries
  const fetchRecentTimeEntries = async () => {
    try {
      setIsLoadingTimeEntries(true);
      if (!employeeId) return;
      const res = await apiFetch(`${BASE_URL}/attendance/${employeeId}/recent`, { method: "GET" });
      if (!res?.ok) return;
      const result = await res.json();
      if (result?.success) setTimeEntries(result.data);
    } catch { /* silent */ } finally { setIsLoadingTimeEntries(false); }
  };

  // stats
  useEffect(() => {
    if (!employeeId) return;
    setLoadingStats(true);
    apiFetch(`${BASE_URL}/attendance/${employeeId}/numbers`, { method: "GET" })
      .then((r) => r.json())
      .then((result) => {
        if (!result.success) return;
        const d = result.data;
        setAttendanceStats([
          { icon: CheckCircle, value: d.present.toString(),                     label: t.stats.presentDays,  color: colors.success, trend: "", subtitle: t.stats.thisMonth },
          { icon: XCircle,     value: d.absent.toString(),                      label: t.stats.absentDays,   color: colors.danger,  trend: "", subtitle: t.stats.thisMonth },
          { icon: AlertCircle, value: d.late.toString(),                        label: t.stats.lateArrivals, color: colors.warning, trend: "", subtitle: t.stats.thisMonth },
          { icon: Timer,       value: d.totalWorkingHours.toFixed(1) + " " + t.units.hours, label: t.stats.totalHours, color: colors.info, trend: "", subtitle: t.stats.thisMonth },
        ]);
      })
      .catch(() => {})
      .finally(() => setLoadingStats(false));
  }, [currentUser, language]);

  // weekly
  useEffect(() => {
    if (!employeeId) return;
    apiFetch(`${BASE_URL}/attendance/${employeeId}/weekly`, { method: "GET" })
      .then((r) => r.json())
      .then((result) => {
        if (!result.success) return;
        setWeeklyData(WEEK_DAYS.map((day) => {
          const rec = result.data.find((r) => new Date(r.date).toLocaleDateString("en-US", { weekday: "long" }) === day);
          const isWeekend = day === "Friday" || day === "Saturday";
          return { day: t.weekDays[day], status: isWeekend ? "weekend" : rec?.status || "absent", checkIn: isWeekend ? "-" : rec?.checkIn || "-", checkOut: isWeekend ? "-" : rec?.checkOut || "-" };
        }));
      })
      .catch(() => {});
  }, [language]);

  // monthly
  useEffect(() => {
    if (!employeeId) return;
    apiFetch(`${BASE_URL}/attendance/${employeeId}/monthly`, { method: "GET" })
      .then((r) => r.json())
      .then((result) => { if (result.success) setMonthlyAttendance(result.data); })
      .catch(() => {});
  }, []);

  // initial today + recent
  useEffect(() => {
    fetchTodayAttendanceRecord();
    fetchRecentTimeEntries();
  }, []);

  const hasTodayCheckIn = useMemo(() => {
    const raw = todayAttendanceRecord?.checkIn;
    return Boolean(raw && String(raw).trim() !== "" && String(raw).trim() !== "-");
  }, [todayAttendanceRecord]);

  const todayCheckInDisplay = useMemo(() => {
    if (!hasTodayCheckIn) return null;
    const value = todayAttendanceRecord?.checkIn;
    if (!value) return null;
    const d = new Date(value);
    if (isNaN(d.getTime())) return String(value);
    return d.toLocaleTimeString(language === "ar" ? "ar-EG" : "en-US", { hour: "2-digit", minute: "2-digit", hour12: true });
  }, [hasTodayCheckIn, todayAttendanceRecord, language]);

  return {
    attendanceStats, loadingStats,
    weeklyData, monthlyAttendance,
    timeEntries, isLoadingTimeEntries,
    todayAttendanceRecord, hasTodayCheckIn, todayCheckInDisplay,
    fetchTodayAttendanceRecord, fetchRecentTimeEntries,
  };
};